import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Button, TextField, Chip, Stack, CircularProgress, Alert,
  useMediaQuery, Divider, Tooltip, IconButton, ToggleButton, ToggleButtonGroup,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TableSortLabel,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaidIcon from "@mui/icons-material/Paid";
import api from "../services/api";
import { todayISO } from "../utils/date";

/* =====================================================================
 * Checking a trucking company's invoice.
 *
 * AJK bills per container move — one line to bring the empty in, one to
 * take the load out. The question is whether the yard agrees that every
 * container on the invoice was really pulled. The yard already knows:
 * every container it ships is entered against a booking number. So the
 * booking number and container number on each of AJK's lines are looked
 * up in the yard's own container records.
 *
 * Drop the file in, read the three numbers at the top, and the lines
 * worth asking about are listed underneath.
 * ===================================================================== */

const money = (n) =>
  "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KIND_LABEL = {
  "already-paid":      "ALREADY PAID",
  "duplicate-in-file": "Billed twice on this invoice",
  "already-billed":    "Already billed before",
  "other-hauler":      "Somebody else hauled it",
  "wrong-booking":     "Booking does not match",
  "not-in-records":    "Not in your records",
  "unpaired":          "Only one half of the move",
};
/* Worst first, matching the order the server reports them in. */
const REASON_RANK = {
  "already-paid": 0, "duplicate-in-file": 1, "already-billed": 2,
  "other-hauler": 3, "wrong-booking": 4, "not-in-records": 5, "unpaired": 6,
};
const KIND_COLOR = {
  "already-paid":      "error",
  "duplicate-in-file": "error",
  "already-billed":    "error",
  "other-hauler":      "error",
  "wrong-booking":     "warning",
  "not-in-records":    "warning",
  "unpaired":          "default",
};

export default function TruckingInvoices() {
  const isMobile = useMediaQuery("(max-width:700px)");
  const fileRef = useRef(null);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [carriers, setCarriers] = useState([]);
  const [carrier, setCarrier] = useState("AJK TRANSPORTATION");
  const [payDialog, setPayDialog] = useState(null);  // the invoice being marked paid
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [result, setResult] = useState(null);   // preview or saved detail
  const [saved, setSaved] = useState(false);    // is `result` already stored?
  const [view, setView] = useState("problems"); // problems | all
  /* Sorted by booking number by default: that is how the invoice gets read.
   * All the moves for one booking sit together, so the empty-in and load-out
   * of the same container are on neighbouring rows and a gap is obvious. */
  const [sortBy, setSortBy] = useState("bookingNo");
  const [sortDir, setSortDir] = useState("asc");

  const flash = (severity, text) => { setMsg({ severity, text }); };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/truckingInvoices")
      .then((r) => setInvoices(r.data || []))
      .catch((e) => flash("error", e?.response?.data?.error || "Could not load the list."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/truckingInvoices/carriers")
      .then((r) => {
        const list = r.data || [];
        setCarriers(list);
        // Default to whoever hauls the most, which is the firm whose invoices
        // are being checked month after month.
        if (list.length) setCarrier(list[0].name);
      })
      .catch(() => { /* leave the box as free text if the list cannot load */ });
  }, []);

  const send = (path, label) => {
    if (!file) { flash("warning", "Choose the invoice file first."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("carrier", carrier);
    fd.append("invoiceNo", invoiceNo);
    fd.append("invoiceDate", invoiceDate);
    setBusy(true);
    setMsg(null);
    api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => {
        if (label === "save") {
          setSaved(true);
          flash("success", r.data.message || "Saved.");
          setResult((prev) => ({ ...(prev || {}), ...r.data }));
          load();
        } else {
          setSaved(false);
          setResult(r.data);
          const n = (r.data.problems || []).filter((p) => p.severity !== "low").length;
          flash(n ? "warning" : "success",
            n ? `${n} line(s) worth asking AJK about. Nothing has been saved yet.`
              : "Every line checks out. Nothing has been saved yet.");
        }
      })
      .catch((e) => flash("error", e?.response?.data?.error || "Could not read that file."))
      .finally(() => setBusy(false));
  };

  const openSaved = (id) => {
    setBusy(true);
    api.get(`/truckingInvoices/${id}`)
      .then((r) => {
        setSaved(true);
        setResult({
          ...r.data,
          carrier: r.data.invoice.Carrier,
          fileName: r.data.invoice.FileName,
          lineCount: r.data.lines.length,
          totalAmount: r.data.invoice.TotalAmount,
        });
        setMsg(null);
      })
      .catch(() => flash("error", "Could not open that invoice."))
      .finally(() => setBusy(false));
  };

  const savePaid = () => {
    const inv = payDialog;
    api.post(`/truckingInvoices/${inv.Invoice_ID}/paid`, {
      paid: true,
      paidDate: inv.payDate,
      paidAmount: inv.payAmount,
      paidRef: inv.payRef,
    })
      .then((r) => { flash("success", r.data.message); setPayDialog(null); load(); })
      .catch((e) => flash("error", e?.response?.data?.error || "Could not save that."));
  };

  const unmarkPaid = (inv) => {
    if (!window.confirm(
      `Mark ${inv.InvoiceNo || "#" + inv.Invoice_ID} as NOT paid again?

` +
      `While it is marked paid, its containers cannot be billed to you again ` +
      `without a warning. Unticking it removes that protection.`)) return;
    api.post(`/truckingInvoices/${inv.Invoice_ID}/paid`, { paid: false })
      .then((r) => { flash("success", r.data.message); load(); })
      .catch((e) => flash("error", e?.response?.data?.error || "Could not save that."));
  };

  const removeSaved = (inv) => {
    if (!window.confirm(
      `Remove ${inv.Carrier} ${inv.InvoiceNo || "#" + inv.Invoice_ID}?\n\n` +
      `Its ${inv.LineCount} lines go too, so later invoices will no longer be ` +
      `checked against it.`)) return;
    api.delete(`/truckingInvoices/${inv.Invoice_ID}`)
      .then(() => { flash("success", "Removed."); setResult(null); load(); })
      .catch((e) => flash("error", e?.response?.data?.error || "Could not remove it."));
  };

  /* EMPTY before LOAD, because that is the real order of the move. */
  const MOVE_ORDER = { EMPTY: 0, LOAD: 1 };
  const sortValue = (row, key) => {
    if (key === "moveType") return MOVE_ORDER[row.moveType] ?? 9;
    if (key === "amount") return Number(row.amount) || 0;
    if (key === "kind") return REASON_RANK[row.kind] ?? 9;
    return String(row[key] ?? "").toUpperCase();
  };
  /* Booking number first, container number second, then EMPTY before LOAD.
   * Whichever column is clicked leads, but the rest of that order still
   * applies underneath it, so rows never shuffle about arbitrarily. */
  const TIE_BREAK = ["bookingNo", "containerNo", "moveType"];
  const sortRows = (rows) => {
    const keys = [sortBy, ...TIE_BREAK.filter((k) => k !== sortBy)];
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      for (const key of keys) {
        const x = sortValue(a, key), y = sortValue(b, key);
        if (x === y) continue;
        // Only the clicked column flips direction; the tie-breaks stay ascending.
        return (x < y ? -1 : 1) * (key === sortBy ? dir : 1);
      }
      return 0;
    });
  };
  const clickSort = (key) => {
    if (sortBy === key) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); return; }
    setSortBy(key);
    setSortDir("asc");
  };
  const SortCell = ({ id, children, align }) => (
    <TableCell align={align} sortDirection={sortBy === id ? sortDir : false}>
      <TableSortLabel active={sortBy === id}
        direction={sortBy === id ? sortDir : "asc"}
        onClick={() => clickSort(id)}>
        {children}
      </TableSortLabel>
    </TableCell>
  );

  const problems = (result?.problems || []);
  const queried = problems.filter((p) => p.severity !== "low");
  const queriedMoney = queried.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const match = result?.match;

  /* The three numbers that answer the question. */
  const headline = match ? [
    { n: match.confirmed,    label: "Confirmed",      hint: "Container, booking and hauler all match your records.", color: "success.main" },
    { n: match.otherHauler,  label: "Someone else hauled", hint: "Your records name a different trucking company for this container — GLOMETS' own truck, or another carrier.", color: "error.main" },
    { n: match.wrongBooking, label: "Wrong booking",  hint: "You have this container, but on a different booking number.", color: "warning.main" },
    { n: match.missing,      label: "Not in records", hint: "No such container in your records for any booking.", color: "error.main" },
  ] : [];

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Trucking Invoices</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drop in the carrier's Excel invoice. Every line's booking number and container
        number is checked against your own container records — including who your
        records say hauled it — and against every invoice you have already uploaded.
      </Typography>

      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      {/* ---------------- upload ---------------- */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems={isMobile ? "stretch" : "flex-end"}>
          <TextField label="Carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)}
            size="small" sx={{ minWidth: 240 }}
            select={carriers.length > 0}
            helperText={carriers.length ? "From your own container records" : " "}>
            {carriers.map((c) => (
              <MenuItem key={c.name} value={c.name}>
                {c.name} &nbsp;<Typography variant="caption" color="text.secondary">
                  ({c.containers} containers)
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Invoice number" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
            size="small" placeholder="Feb-26" sx={{ minWidth: 140 }} />
          <TextField label="Invoice date" type="date" value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)} size="small"
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          <Button variant="outlined" startIcon={<CloudUploadIcon />} component="label" sx={{ whiteSpace: "nowrap" }}>
            {file ? "Change file" : "Choose file"}
            <input hidden type="file" accept=".xls,.xlsx,.csv" ref={fileRef}
              onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); setMsg(null); }} />
          </Button>
        </Stack>

        {file && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {file.name}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" disabled={busy || !file} onClick={() => send("/truckingInvoices/preview", "preview")}>
            {busy ? <CircularProgress size={20} /> : "Check it"}
          </Button>
          <Tooltip title="Keep this invoice, so next month's is checked against it too">
            <span>
              <Button variant="outlined" disabled={busy || !file || saved}
                onClick={() => send("/truckingInvoices", "save")}>
                Save &amp; keep
              </Button>
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          "Check it" reads the file and tells you what it finds without storing anything.
          Save it once you are happy — only saved invoices are used to catch a container
          being billed again in a later month.
        </Typography>
      </Paper>

      {/* ---------------- result ---------------- */}
      {result && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={isMobile ? "column" : "row"} spacing={1} justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"} sx={{ mb: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>
                {result.carrier} {result.invoice?.InvoiceNo || invoiceNo || ""}
                {saved ? "" : "  — not saved yet"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {result.lineCount} moves · {money(result.totalAmount)}
                {result.periodFrom ? ` · ${result.periodFrom} to ${result.periodTo}` : ""}
              </Typography>
            </Box>
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)}>
              <ToggleButton value="problems">Worth asking ({queried.length})</ToggleButton>
              <ToggleButton value="all">Every line ({result.lineCount})</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* the three numbers */}
          {!!headline.length && (
            <>
              <Stack direction="row" spacing={isMobile ? 2 : 4} sx={{ my: 2, flexWrap: "wrap" }}>
                {headline.map((h) => (
                  <Tooltip key={h.label} title={h.hint}>
                    <Box sx={{ cursor: "help" }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: h.color }}>{h.n}</Typography>
                      <Typography variant="caption" color="text.secondary">{h.label}</Typography>
                    </Box>
                  </Tooltip>
                ))}
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{money(queriedMoney)}</Typography>
                  <Typography variant="caption" color="text.secondary">Worth querying</Typography>
                </Box>
              </Stack>

              {match.missing > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Read "not in records" carefully. Your container records only hold what
                  was entered in the app
                  {result.yardContainers != null
                    ? ` — ${result.yardContainers} container${result.yardContainers === 1 ? "" : "s"} in these dates`
                    : ""}
                  , so a container can be missing because nobody typed it, not because
                  AJK did not pull it. The more the yard enters, the sharper this gets.
                </Alert>
              )}
            </>
          )}

          {problems.some((p) => p.kind === "already-paid") && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>You have already paid for some of these moves.</strong>{" "}
              They are on an invoice you marked paid, so paying this one in full
              would pay twice. The lines are marked ALREADY PAID below.
            </Alert>
          )}

          <Divider sx={{ mb: 1 }} />

          {view === "problems" ? (
            queried.length === 0 && problems.length === 0 ? (
              <Alert severity="success">Nothing to query. Every line matches your records.</Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <SortCell id="kind">What</SortCell>
                      <SortCell id="bookingNo">Booking #</SortCell>
                      <SortCell id="containerNo">Container</SortCell>
                      <SortCell id="moveType">Move</SortCell>
                      <SortCell id="amount" align="right">Amount</SortCell>
                      <TableCell>Detail</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortRows(problems).map((p, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Chip size="small" label={KIND_LABEL[p.kind] || p.kind}
                            color={KIND_COLOR[p.kind] || "default"} />
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>{p.bookingNo || "—"}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>{p.containerNo}</TableCell>
                        <TableCell>{p.moveType || "—"}</TableCell>
                        <TableCell align="right">{p.amount ? money(p.amount) : "—"}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{p.detail}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : (
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <SortCell id="bookingNo">Booking #</SortCell>
                    <SortCell id="containerNo">Container</SortCell>
                    <SortCell id="moveType">Move</SortCell>
                    <SortCell id="moveDate">Date</SortCell>
                    <SortCell id="driver">Driver</SortCell>
                    <SortCell id="amount" align="right">Amount</SortCell>
                    <SortCell id="matchStatus">Your records</SortCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortRows(result.lines || []).map((l, i, arr) => (
                    // A faint rule between bookings, so each booking reads as a block.
                    <TableRow key={i} hover sx={sortBy === "bookingNo" &&
                      i > 0 && arr[i - 1].bookingNo !== l.bookingNo
                      ? { "& td": { borderTop: "2px solid", borderTopColor: "divider" } }
                      : undefined}>
                      <TableCell sx={{ fontFamily: "monospace" }}>{l.bookingNo || "—"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>{l.containerNo}</TableCell>
                      <TableCell>{l.moveType || "—"}</TableCell>
                      <TableCell>{l.moveDate || "—"}</TableCell>
                      <TableCell>{l.driver || "—"}</TableCell>
                      <TableCell align="right">{money(l.amount)}</TableCell>
                      <TableCell>
                        {l.matchStatus === "confirmed" && (
                          <Chip size="small" color="success" label="Confirmed" />
                        )}
                        {l.matchStatus === "other-hauler" && (
                          <Chip size="small" color="error" label={l.appHauler || "Other hauler"} />
                        )}
                        {l.matchStatus === "wrong-booking" && (
                          <Chip size="small" color="warning" label={`On ${l.appBookingNo}`} />
                        )}
                        {l.matchStatus === "missing" && (
                          <Chip size="small" color="error" variant="outlined" label="Not found" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ---------------- history ---------------- */}
      <Paper sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Invoices kept</Typography>
        {loading ? <CircularProgress size={24} /> : invoices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing saved yet. Once you save one, the next month's invoice is checked
            against it — that is how a container billed in two different months is caught.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Carrier</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Dates covered</TableCell>
                  <TableCell align="right">Moves</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Paid?</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.Invoice_ID} hover sx={{ cursor: "pointer" }}>
                    <TableCell onClick={() => openSaved(inv.Invoice_ID)}>{inv.Carrier}</TableCell>
                    <TableCell onClick={() => openSaved(inv.Invoice_ID)}>
                      {inv.InvoiceNo || `#${inv.Invoice_ID}`}
                    </TableCell>
                    <TableCell onClick={() => openSaved(inv.Invoice_ID)}>
                      {inv.PeriodFrom ? `${inv.PeriodFrom} → ${inv.PeriodTo}` : "—"}
                    </TableCell>
                    <TableCell align="right" onClick={() => openSaved(inv.Invoice_ID)}>{inv.LineCount}</TableCell>
                    <TableCell align="right" onClick={() => openSaved(inv.Invoice_ID)}>{money(inv.TotalAmount)}</TableCell>
                    <TableCell>
                      {inv.Paid ? (
                        <Tooltip title={
                          `Paid ${inv.PaidDate || ""}` +
                          (inv.PaidRef ? ` · ref ${inv.PaidRef}` : "") +
                          (inv.PaidAmount != null ? ` · ${money(inv.PaidAmount)}` : "") +
                          " — its containers are settled"}>
                          <Chip size="small" color="success" label="Paid"
                            onClick={() => unmarkPaid(inv)} />
                        </Tooltip>
                      ) : (
                        <Button size="small" startIcon={<PaidIcon />}
                          onClick={() => setPayDialog({
                            ...inv,
                            payDate: todayISO(),
                            payAmount: inv.TotalAmount,
                            payRef: "",
                          })}>
                          Mark paid
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={inv.Paid
                        ? "Marked paid — mark it unpaid first to remove it"
                        : "Remove this invoice"}>
                        <IconButton size="small" disabled={!!inv.Paid}
                          onClick={() => removeSaved(inv)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ---------------- mark paid ---------------- */}
      <Dialog open={!!payDialog} onClose={() => setPayDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          Mark {payDialog?.InvoiceNo || `#${payDialog?.Invoice_ID}`} paid
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Once it is marked paid, every container on it counts as settled. If
            {" "}{payDialog?.Carrier} bills one of them on a later invoice, the
            check will say ALREADY PAID instead of just "seen before".
          </Typography>
          <Stack spacing={2}>
            <TextField label="Date paid" type="date" size="small"
              InputLabelProps={{ shrink: true }}
              value={payDialog?.payDate || ""}
              onChange={(e) => setPayDialog((d) => ({ ...d, payDate: e.target.value }))} />
            <TextField label="Amount paid" type="number" size="small"
              value={payDialog?.payAmount ?? ""}
              onChange={(e) => setPayDialog((d) => ({ ...d, payAmount: e.target.value }))}
              helperText="Leave as it is unless you paid a different amount" />
            <TextField label="Cheque number or ACH reference" size="small"
              value={payDialog?.payRef || ""}
              onChange={(e) => setPayDialog((d) => ({ ...d, payRef: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={savePaid}>Mark paid</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
